import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { Button, ButtonLink, RequiredMarker, Tag, cx } from '../../../components/idsk';
import { RuleSummarizer, flatten, mergeDefinition, type EvaluationContext, type FieldDef, type FormDefinition, type FormValues, type RuleName } from '../../../form-engine';
import { FormRenderer, type Lang } from '../../../form-engine/react/FormRenderer';
import { canBeRequired, fallsBackToSlovak, text } from '../../../form-engine/types';
import { useAnnouncer } from '../../../layout/Announcer';
import { toast } from '../../../components/common/toasts';
import { locate, moveCustomFieldTo, moveFieldTo, newOrderErrors, validateFor } from './definitionOps';
import { PROPERTIES_HEADING, type BuilderMode } from './FieldEditor';
import { allowedIn, isGroup, moveFits, resolveDrop, staysInPlace, type DropPosition, type PaletteItem } from './palette';
import { builderDragOf, endBuilderDrag, startFieldDrag, useBuilderDrag, type BuilderDrag } from './builderDrag';

const RULES: RuleName[] = ['visibleWhen', 'enabledWhen', 'requiredWhen', 'validWhen'];
const NO_VALUES: FormValues = {};
const noop = () => undefined;
/** id prefix of the drawn controls: {CANVAS}-{key} (the properties pane uses field-…) */
const CANVAS = 'canvas';

/** The id of the "Upraviť …" link of a field on the canvas (the focus returns there from the properties pane). */
export const canvasEditId = (key: string) => `${CANVAS}-edit-${key}`;

type Change = { definition: FormDefinition; customFields: FieldDef[] };

/** How a drop place is shown: an insertion line, the outlined block of a group, or the end zone. */
type DropMark = { kind: 'line'; top: number; left: number; width: number } | { kind: 'group'; top: number; left: number; width: number; height: number } | { kind: 'end' };

/** Where the dragged control or field would go; `blocked`: why a field cannot be moved there (no drop). */
interface DropIndicator {
  dragId: number;
  position: DropPosition;
  mark: DropMark;
  blocked: string | null;
}

export interface FormCanvasProps {
  mode: BuilderMode;
  /** template mode: the draft; custom mode: the pinned template definition */
  definition: FormDefinition;
  customFields: FieldDef[];
  context: EvaluationContext;
  /** route of the properties pane of a field */
  selectPath: (key: string) => string;
  /** route of the palette page (a new field, optionally into a group) */
  newPath: (parentKey: string | null) => string;
  /** the field whose properties are open */
  selected: string | null;
  /**
   * The field being edited as it would be saved (shown live in place of the selected field); with `position` a new
   * field, shown at that place.
   */
  preview?: { field: FieldDef; position?: DropPosition } | null;
  /** Route of the properties pane of a control dropped from the palette at `index` of the top level or of `parentKey`. */
  dropPath?: (item: PaletteItem, parentKey: string | null, index: number) => string;
  /** Persists a definition (template) or custom fields (custom) after a field was moved by dragging; false when not saved. */
  onChange?: (next: Change) => Promise<boolean>;
}

/** A field drawn by the form renderer itself – without its rules, so it and all its options are shown and available. */
function FieldPreview({ field, context, lang }: { field: FieldDef; context: EvaluationContext; lang: Lang }) {
  const definition = useMemo(() => {
    const plain = { ...field };
    delete plain.visibleWhen;
    delete plain.enabledWhen;
    delete plain.requiredWhen;
    delete plain.validWhen;
    if (plain.options) plain.options = plain.options.map((o) => ({ value: o.value, label: o.label }));
    return { fields: [plain] };
  }, [field]);
  return <FormRenderer formType={CANVAS} definition={definition} values={NO_VALUES} onChange={noop} context={context} lang={lang} showErrors={false} />;
}

/**
 * The WYSIWYG canvas of the form builder: every field drawn as the form shows it (the form renderer itself, not
 * operable – `inert`), sections and repeating groups with their fields inside. A click on a field opens its
 * properties (the "Upraviť …" link of each field is the way for the keyboard); the selected field is marked and shows
 * the edited properties live. Rules are listed under their field (in a form they hide or disable it).
 *
 * Pointer extras: fields are moved by dragging their grip and palette controls are dropped onto the canvas – before or
 * after a field (a line with its text), onto a section or repeating group (into it, at the end; the group is
 * outlined) or onto the end zone. A move that would make a rule refer to a later field is refused (a red line says
 * why). The properties pane offers the same moves without dragging (WCAG 2.5.7).
 */
export function FormCanvas({ mode, definition, customFields, context, selectPath, newPath, selected, preview, dropPath, onChange }: FormCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  const { alert } = useAnnouncer();
  const navigate = useNavigate();
  const drag = useBuilderDrag();
  const [drop, setDrop] = useState<DropIndicator | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const effective = mode === 'template' ? definition : mergeDefinition(definition, customFields);
  const summarizer = new RuleSummarizer(effective, context);
  const label = (f: FieldDef) => text(f.label, lang);
  const editable = (f: FieldDef) => mode === 'template' || f.origin === 'INSPECTION';
  const acceptsPalette = Boolean(dropPath);
  const acceptsMoves = Boolean(onChange);
  // the indicator of an earlier drag is never shown
  const indicator = drag && drop?.dragId === drag.id ? drop : null;
  // team members add and move only to the top level and to their own sections and repeating groups
  const canDropIn = (group: FieldDef | null) => group === null || mode === 'template' || group.origin === 'INSPECTION';
  const newField = preview?.position ? { field: preview.field, position: preview.position } : null;
  const newAt = newField ? `${newField.position.parentKey ?? ''}:${newField.position.index}` : null;

  // the selected field (or the new one) in view
  useEffect(() => {
    const selector = selected ? `[data-block-key="${selected}"]` : newAt ? '[data-block-new]' : null;
    if (selector) root.current?.querySelector(selector)?.scrollIntoView?.({ block: 'nearest' });
  }, [selected, newAt]);

  // ------------------------------------------------------------------------------------------- dragging

  const accepts = (d: BuilderDrag) => {
    if (d.kind === 'palette') return acceptsPalette;
    const f = locate(effective.fields, d.key)?.field;
    return acceptsMoves && Boolean(f && editable(f));
  };

  /** The definition after moving field `key` to `p` (null when it stays). */
  const moveTo = (key: string, p: DropPosition): Change | null => {
    if (mode === 'template') {
      const moved = moveFieldTo(definition, key, p.parentKey, p.index);
      return moved ? { definition: moved, customFields } : null;
    }
    const moved = moveCustomFieldTo(definition, customFields, key, p.parentKey, p.index);
    return moved ? { definition, customFields: moved } : null;
  };

  /** Why moving field `key` there is not possible: a rule would refer to a later field. */
  const blockedMove = (key: string, next: Change): string | null => {
    const nextEffective = mode === 'template' ? next.definition : mergeDefinition(next.definition, next.customFields);
    const introduced = newOrderErrors(validateFor(mode, effective, definition, customFields), validateFor(mode, nextEffective, next.definition, next.customFields));
    if (!introduced.length) return null;
    const dependent = locate(nextEffective.fields, introduced[0]!.field)?.field;
    return t('builder.move.blocked', { label: label(locate(effective.fields, key)!.field), dependent: dependent ? label(dependent) : introduced[0]!.field });
  };

  /** Where the line or the outline goes, relative to the canvas. */
  const markOf = (canvas: HTMLElement, p: DropPosition, kind: 'line' | 'group'): DropMark | null => {
    const box = canvas.getBoundingClientRect();
    const rectOf = (selector: string) => canvas.querySelector(selector)?.getBoundingClientRect();
    if (kind === 'group') {
      const r = rectOf(`[data-block-key="${p.parentKey}"]`);
      return r ? { kind: 'group', top: r.top - box.top, left: r.left - box.left, width: r.width, height: r.height } : null;
    }
    const siblings = p.parentKey ? (locate(effective.fields, p.parentKey)?.field.children ?? []) : effective.fields;
    const next = siblings[p.index] ? rectOf(`[data-block-key="${siblings[p.index]!.key}"]`) : undefined;
    const prev = siblings[p.index - 1] ? rectOf(`[data-block-key="${siblings[p.index - 1]!.key}"]`) : undefined;
    const ref = next ?? prev ?? rectOf(`[data-drop-into="${p.parentKey}"]`);
    if (!ref) return null;
    // between the two blocks, else just above the next or below the previous one
    const y = next && prev ? (prev.top + prev.height + next.top) / 2 : next ? next.top - 6 : prev ? prev.top + prev.height + 6 : ref.top + 6;
    return { kind: 'line', top: y - box.top, left: ref.left - box.left, width: ref.width };
  };

  const dropTargetOf = (e: React.DragEvent<HTMLDivElement>, d: BuilderDrag): { position: DropPosition; mark: DropMark } | null => {
    const el = e.target instanceof Element ? e.target : (e.target as Node | null)?.parentElement;
    if (!el) return null;
    const fits = d.kind === 'palette' ? (g: FieldDef | null) => canDropIn(g) && allowedIn(d.item.type, g?.type ?? null) : moveFits(effective.fields, d.key, canDropIn);
    let position: DropPosition | null = null;
    let kind: 'line' | 'group' | 'end' = 'line';
    const keyed = el.closest<HTMLElement>('[data-drop-key]');
    const into = el.closest<HTMLElement>('[data-drop-into]');
    if (el.closest('.app-drop-end')) {
      if (fits(null)) position = { parentKey: null, index: effective.fields.length };
      kind = 'end';
    } else if (keyed && (!into || into.contains(keyed))) {
      // a field, or the top of a group (its bar and heading)
      const rect = keyed.getBoundingClientRect();
      const found = resolveDrop(effective.fields, keyed.dataset.dropKey!, rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0.5, fits);
      if (found) {
        position = found.position;
        kind = found.mark.kind;
      }
    } else if (into) {
      // the room around the fields of a group: into it, at the end
      const group = locate(effective.fields, into.dataset.dropInto!)?.field;
      if (group && fits(group)) {
        position = { parentKey: group.key, index: group.children?.length ?? 0 };
        kind = 'group';
      }
    }
    if (!position || (d.kind === 'field' && staysInPlace(effective.fields, d.key, position))) return null;
    const mark = kind === 'end' ? ({ kind: 'end' } as const) : markOf(e.currentTarget, position, kind);
    return mark ? { position, mark } : null;
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const dragged = builderDragOf(e);
    if (!dragged || !accepts(dragged)) return;
    const target = dropTargetOf(e, dragged);
    if (!target) {
      setDrop(null);
      return;
    }
    const same =
      drop !== null && drop.dragId === dragged.id && drop.position.parentKey === target.position.parentKey && drop.position.index === target.position.index && JSON.stringify(drop.mark) === JSON.stringify(target.mark);
    let blocked = same ? (drop?.blocked ?? null) : null;
    if (!same && dragged.kind === 'field') {
      const next = moveTo(dragged.key, target.position);
      blocked = next ? blockedMove(dragged.key, next) : null;
    }
    // a blocked place takes no drop (the cursor says so, the line says why)
    if (!blocked) {
      e.preventDefault();
      e.dataTransfer.dropEffect = dragged.kind === 'palette' ? 'copy' : 'move';
    }
    if (!same) setDrop({ dragId: dragged.id, ...target, blocked });
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    const dragged = builderDragOf(e);
    if (!dragged || !accepts(dragged)) return;
    // where the pointer is (the place the line shows)
    const target = dropTargetOf(e, dragged);
    if (!target) return;
    e.preventDefault();
    setDrop(null);
    endBuilderDrag();
    if (dragged.kind === 'palette') {
      if (dropPath) void navigate(dropPath(dragged.item, target.position.parentKey, target.position.index), { state: { focus: PROPERTIES_HEADING } });
      return;
    }
    void moveByDrop(dragged.key, target.position);
  };

  const moveByDrop = async (key: string, p: DropPosition) => {
    const next = moveTo(key, p);
    if (!next) return;
    const blocked = blockedMove(key, next);
    if (blocked) return alert(blocked);
    const before = locate(effective.fields, key)!;
    if (!(await onChange?.(next))) return;
    const nextEffective = mode === 'template' ? next.definition : mergeDefinition(next.definition, next.customFields);
    const loc = locate(nextEffective.fields, key)!;
    const name = label(before.field);
    if ((before.parent?.key ?? null) === (loc.parent?.key ?? null)) toast.success(t('builder.list.moved', { label: name, position: loc.index + 1, count: loc.siblings.length }));
    else if (loc.parent) toast.success(t('builder.move.movedInto', { label: name, section: label(loc.parent) }));
    else toast.success(t('builder.move.movedOut', { label: name }));
  };

  /** Text of the insertion line or the outlined group: "Vložiť na 2. miesto v sekcii Škola". */
  const dropText = (d: BuilderDrag | null, p: DropPosition, mark: DropMark) => {
    const keys = d?.kind === 'field' ? 'builder.list.dropMove' : 'builder.list.drop';
    const group = p.parentKey ? locate(effective.fields, p.parentKey)?.field : null;
    if (!group) return t(`${keys}.form`, { position: p.index + 1 });
    const section = group.type === 'SECTION';
    if (mark.kind === 'group') return t(`${keys}.${section ? 'intoSection' : 'intoGroup'}`, { label: label(group) });
    return t(`${keys}.${section ? 'section' : 'group'}`, { position: p.index + 1, label: label(group) });
  };

  // ------------------------------------------------------------------------------------------- blocks

  const open = (key: string) => void navigate(selectPath(key), { state: { focus: PROPERTIES_HEADING } });

  /** The top of a group as the form shows it (heading and hint); its fields follow as blocks of their own. */
  const groupHead = (f: FieldDef) => {
    const name = text(f.label, lang);
    const nameLang = fallsBackToSlovak(f.label, lang) ? 'sk' : undefined;
    const hint = f.hint && text(f.hint, lang) ? <p className="govuk-hint">{text(f.hint, lang)}</p> : null;
    if (f.type === 'SECTION') {
      return (
        <>
          <div className="govuk-fieldset__legend govuk-fieldset__legend--m">
            <p className="govuk-fieldset__heading" lang={nameLang}>
              {name}
            </p>
          </div>
          {hint}
        </>
      );
    }
    return (
      <>
        <p className="govuk-heading-m" lang={nameLang}>
          {name}
          <RequiredMarker required={f.required} />
        </p>
        {hint}
      </>
    );
  };

  const block = (f: FieldDef, isNew = false): ReactNode => {
    // the field being edited shows its edited properties
    const shown = isNew ? f : selected === f.key && preview && !preview.position ? { ...preview.field, children: f.children } : f;
    const own = editable(f) && !isNew;
    const current = isNew || selected === f.key;
    const rules = [
      ...RULES.map((r) => (canBeRequired(shown.type) || r !== 'requiredWhen' ? summarizer.summaryOf(shown, r, lang) : null)),
      // "Možnosť „Iné“ sa zobrazí, ak …"
      ...(shown.options ?? []).map((o) => summarizer.optionSummary(o, lang)),
    ].filter((s): s is string => Boolean(s));
    const rulesId = `${CANVAS}-${isNew ? 'new' : f.key}-rules`;
    const group = isGroup(shown);
    const bar = (
      <div className="app-canvas-block__bar">
        {acceptsMoves && own && (
          <span
            className="app-field-grip"
            draggable
            onDragStart={(e) => startFieldDrag(e, f.key, e.currentTarget.closest('.app-canvas-block'))}
            onDragEnd={endBuilderDrag}
            title={t('builder.list.dragHandle', { label: label(shown) })}
            aria-hidden="true"
          >
            <svg viewBox="0 0 10 16" width="10" height="16" focusable="false">
              {[2, 8, 14].flatMap((y) => [2, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
            </svg>
          </span>
        )}
        <span className="app-canvas-block__type">{t(`builder.types.${shown.type}.name`)}</span>
        {isNew && <Tag colour="blue">{t('builder.canvas.newTag')}</Tag>}
        {!isNew && f.origin === 'INSPECTION' && <Tag colour="purple">{t('builder.list.customTag')}</Tag>}
        {shown.chart && <Tag colour="turquoise">{t('builder.canvas.chartTag')}</Tag>}
        {own ? (
          <Link
            id={canvasEditId(f.key)}
            className="govuk-link app-canvas-block__edit"
            to={selectPath(f.key)}
            state={{ focus: PROPERTIES_HEADING }}
            aria-current={current ? 'true' : undefined}
            aria-describedby={rules.length ? rulesId : undefined}
            onClick={(e) => e.stopPropagation()}
          >
            {t('common.edit')} <span className="govuk-visually-hidden">{label(shown)}</span>
          </Link>
        ) : (
          !isNew && mode === 'custom' && <span className="app-canvas-block__note">{t('builder.list.templateField')}</span>
        )}
      </div>
    );
    const rulesList = rules.length > 0 && (
      <ul className="govuk-list govuk-!-font-size-16 app-canvas-block__rules" id={rulesId}>
        {rules.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
    );
    return (
      // a click anywhere on a field opens its properties; its "Upraviť …" link is the way for the keyboard
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
      <li
        key={isNew ? 'new' : f.key}
        className={cx('app-canvas-block', group && 'app-canvas-block--group', own && 'app-canvas-block--editable', current && 'app-canvas-block--current', drag?.kind === 'field' && drag.key === f.key && 'app-canvas-block--dragging')}
        data-block-key={isNew ? undefined : f.key}
        data-block-new={isNew ? '' : undefined}
        data-drop-key={!group && !isNew ? f.key : undefined}
        onClick={
          own
            ? (e) => {
                e.stopPropagation();
                open(f.key);
              }
            : (e) => e.stopPropagation()
        }
      >
        {group ? (
          <>
            <div className="app-canvas-block__top" data-drop-key={isNew ? undefined : f.key}>
              {bar}
              <div className="app-canvas-block__preview" inert>
                {groupHead(shown)}
              </div>
            </div>
            <div className={cx('app-canvas-block__fields', shown.type === 'REPEATING_GROUP' && 'app-repeating-row')} data-drop-into={isNew ? undefined : f.key}>
              {shown.type === 'REPEATING_GROUP' && (
                <p className="govuk-fieldset__legend govuk-fieldset__legend--s app-canvas-block__preview" inert>
                  {t('form.rowLegend', { label: label(shown), n: 1 })}
                </p>
              )}
              {f.children?.length || (!isNew && newField && newField.position.parentKey === f.key) ? (
                // the new field itself is placed once (never inside its own block)
                <ul className="app-canvas__list">{isNew ? (f.children ?? []).map((c) => block(c)) : list(f.children ?? [], f.key)}</ul>
              ) : (
                <p className="govuk-body app-canvas-block__empty">{t('builder.list.emptyGroup')}</p>
              )}
            </div>
            {shown.type === 'REPEATING_GROUP' && (
              <div className="app-canvas-block__preview" inert>
                <Button variant="secondary" className="govuk-!-margin-bottom-0">
                  {t('form.addRow')}
                </Button>
              </div>
            )}
          </>
        ) : (
          <>
            {bar}
            <div className="app-canvas-block__preview" inert>
              <FieldPreview field={shown} context={context} lang={lang} />
            </div>
          </>
        )}
        {rulesList}
      </li>
    );
  };

  /** The blocks of a list of fields, with the new field at its place. */
  const list = (fields: FieldDef[], parentKey: string | null): ReactNode[] => {
    const items = fields.map((f) => block(f));
    if (newField && newField.position.parentKey === parentKey) items.splice(Math.min(newField.position.index, items.length), 0, block(newField.field, true));
    return items;
  };

  const listening = acceptsPalette || acceptsMoves;
  return (
    <section className="app-canvas-section" aria-labelledby={`${CANVAS}-heading`}>
      <h2 className="govuk-heading-m" id={`${CANVAS}-heading`}>
        {t('builder.list.caption', { count: flatten(effective.fields).length })}
      </h2>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- drop target (pointer only; the palette links and the actions in the properties pane work without dragging) */}
      <div
        ref={root}
        className="app-canvas"
        onDragOver={listening ? onDragOver : undefined}
        onDrop={listening ? onDrop : undefined}
        onDragLeave={
          listening
            ? (e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrop(null);
              }
            : undefined
        }
      >
        <ul className="app-canvas__list">{list(effective.fields, null)}</ul>
        {indicator?.mark.kind === 'line' && (
          <div
            className={cx('app-drop-line', indicator.blocked && 'app-drop-line--blocked')}
            style={{ top: indicator.mark.top, left: indicator.mark.left, width: indicator.mark.width }}
            aria-hidden="true"
          >
            <span className="app-drop-line__text">{indicator.blocked ?? dropText(drag, indicator.position, indicator.mark)}</span>
          </div>
        )}
        {indicator?.mark.kind === 'group' && (
          <div
            className={cx('app-drop-group', indicator.blocked && 'app-drop-group--blocked')}
            style={{ top: indicator.mark.top, left: indicator.mark.left, width: indicator.mark.width, height: indicator.mark.height }}
            aria-hidden="true"
          >
            <span className="app-drop-line__text">{indicator.blocked ?? dropText(drag, indicator.position, indicator.mark)}</span>
          </div>
        )}
        {(acceptsPalette || drag?.kind === 'field') && (
          <div className={cx('app-drop-end', indicator?.mark.kind === 'end' && (indicator.blocked ? 'app-drop-end--blocked' : 'app-drop-end--active'))} aria-hidden="true">
            {indicator?.mark.kind === 'end' && indicator.blocked ? indicator.blocked : t(drag?.kind === 'field' ? 'builder.list.dropMove.end' : 'builder.list.drop.end')}
          </div>
        )}
      </div>
      <p className="govuk-body">
        <ButtonLink id="add-field" to={newPath(null)} variant="secondary">
          {mode === 'template' ? t('builder.list.addField') : t('builder.list.addCustomField')}
        </ButtonLink>
      </p>
    </section>
  );
}
